/**
 * Schedule Service
 * Synchronized with backend/internal/handlers/schedule_handler.go
 */
import { apiClient } from './api'
import type { ApiResponse } from '@/types/common'
import type {
  Schedule,
  CreateScheduleInput,
  UpdateScheduleInput,
} from '@/types/schedule'

export const scheduleService = {
  /**
   * Create a new schedule for a batch
   * POST /api/v1/batches/:batchId/schedules
   */
  createSchedule: async (
    batchId: string,
    input: CreateScheduleInput
  ): Promise<ApiResponse<Schedule>> => {
    return apiClient.post(`/batches/${batchId}/schedules`, input)
  },

  /**
   * Get all schedules for a specific batch
   * GET /api/v1/batches/:batchId/schedules
   */
  getSchedulesByBatch: async (batchId: string): Promise<ApiResponse<Schedule[]>> => {
    return apiClient.get(`/batches/${batchId}/schedules`)
  },

  /**
   * Get a single schedule by ID
   * GET /api/v1/schedules/:id
   */
  getScheduleById: async (id: string): Promise<ApiResponse<Schedule>> => {
    return apiClient.get(`/schedules/${id}`)
  },

  /**
   * Update an existing schedule
   * PUT /api/v1/schedules/:id
   */
  updateSchedule: async (
    id: string,
    input: UpdateScheduleInput
  ): Promise<ApiResponse<{ message: string }>> => {
    return apiClient.put(`/schedules/${id}`, input)
  },

  /**
   * Delete a schedule
   * DELETE /api/v1/schedules/:id
   */
  deleteSchedule: async (id: string): Promise<ApiResponse<{ message: string }>> => {
    return apiClient.delete(`/schedules/${id}`)
  },

  /**
   * Get upcoming schedules for an instructor
   * GET /api/v1/schedules/upcoming?instructor_id=xxx&limit=10
   */
  getUpcomingSchedules: async (
    instructorId: string,
    limit?: number
  ): Promise<ApiResponse<Schedule[]>> => {
    const params: Record<string, string | number> = { instructor_id: instructorId }
    if (limit) {
      params.limit = limit
    }
    return apiClient.get('/schedules/upcoming', { params })
  },
}

export default scheduleService
